import React, { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { library } from "@fortawesome/fontawesome-svg-core";
import { fas } from "@fortawesome/free-solid-svg-icons";
import { reportingGoals } from "../../src/reportingGoals";

library.add(fas);

const HORIZONTAL_PADDING = "15px";
const COLUMN_GUTTER = "8px";
const CAMPAIGN_INSIGHT_SECTION_PADDING = "20px";
const OPTIMIZATION_IDEA_PREVIEW_MAX_LENGTH = 180;
const OVERVIEW_TEXT_STYLE = { lineHeight: 1.85 };
const REMIX_GRADIENT_BORDER_STYLE = {
  background: `
    linear-gradient(white, white) padding-box,
    linear-gradient(90deg, #6B8CFF 0%, #003FFF 100%) border-box
  `,
  border: "1px solid transparent",
};

const formatNumber = (value, options = {}) => {
  const numericValue = Number(value);
  if (Number.isNaN(numericValue)) {
    return "0";
  }

  return numericValue.toLocaleString(undefined, options);
};

const formatCurrency = (value, currency) => {
  return `${currency || ""}${formatNumber(value, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })}`;
};

const formatPercent = (value) => {
  const numericValue = Number(value);
  if (Number.isNaN(numericValue)) {
    return "0%";
  }

  return `${numericValue.toFixed(2)}%`;
};

const changeBadge = (change) => {
  const value = change == null ? null : Number(change);
  const roundedValue = value == null ? null : Math.round(value);
  const label = roundedValue == null ? "—" : `${Math.abs(roundedValue)}%`;
  const isZero = roundedValue == null || roundedValue === 0;
  const style = {
    display: "inline-flex",
    alignItems: "center",
    padding: "2px 8px",
    borderRadius: "999px",
    fontSize: "11px",
    fontWeight: 500,
    marginTop: "4px",
    background: isZero ? "#f3f4f6" : roundedValue > 0 ? "#dcfce7" : "#fee2e2",
    color: isZero ? "#374151" : roundedValue > 0 ? "#166534" : "#991b1b",
  };

  const arrow =
    roundedValue > 0 ? (
      <svg
        style={{
          width: "12px",
          height: "12px",
          marginRight: "4px",
          transform: "rotate(-45deg)",
        }}
        fill="currentColor"
        viewBox="0 0 20 20"
        aria-hidden="true"
        focusable="false"
      >
        <path
          fillRule="evenodd"
          d="M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z"
          clipRule="evenodd"
        />
      </svg>
    ) : roundedValue < 0 ? (
      <svg
        style={{
          width: "12px",
          height: "12px",
          marginRight: "4px",
          transform: "rotate(45deg)",
        }}
        fill="currentColor"
        viewBox="0 0 20 20"
        aria-hidden="true"
        focusable="false"
      >
        <path
          fillRule="evenodd"
          d="M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z"
          clipRule="evenodd"
        />
      </svg>
    ) : null;

  return (
    <span style={style}>
      {arrow}
      {label}
    </span>
  );
};

const formatAnalysisPeriod = (analysisPeriod) => {
  if (!analysisPeriod?.start || !analysisPeriod?.end) {
    return null;
  }

  const start = new Date(analysisPeriod.start);
  const end = new Date(analysisPeriod.end);

  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
    return null;
  }

  const formatter = new Intl.DateTimeFormat(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });

  return `${formatter.format(start)} – ${formatter.format(end)}`;
};

const buildApplyExperimentUrl = (slide, idea) => {
  if (
    !slide?.variant_id ||
    slide.optimization_ideas_are_placeholder ||
    idea?.placeholder
  ) {
    return null;
  }

  const params = new URLSearchParams({
    open_duplicate_remix: "1",
    variant_id: String(slide.variant_id),
    remix_instructions: idea.description || "",
    variant_name: idea.title || "",
  });

  return `${slide.campaign_url}?${params.toString()}#stats`;
};

const RemixSparkleIcon = () => (
  <svg
    width="13"
    height="14"
    viewBox="0 0 13 14"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
    style={{ flexShrink: 0 }}
  >
    <path
      d="M10.5086 7.86766L8.97152 7.42808C8.27936 7.23279 7.64886 6.86299 7.14034 6.35408C6.63183 5.84516 6.26232 5.21416 6.06718 4.52145L5.62795 2.9832C5.60586 2.92939 5.56829 2.88336 5.52 2.85096C5.47171 2.81857 5.41488 2.80127 5.35675 2.80127C5.29861 2.80127 5.24179 2.81857 5.1935 2.85096C5.14521 2.88336 5.10763 2.92939 5.08554 2.9832L4.64631 4.52145C4.45117 5.21416 4.08166 5.84516 3.57315 6.35408C3.06464 6.86299 2.43413 7.23279 1.74197 7.42808L0.204942 7.86766C0.145925 7.88443 0.0939831 7.92 0.0569971 7.96898C0.0200111 8.01797 0 8.07769 0 8.13908C0 8.20048 0.0200111 8.2602 0.0569971 8.30918C0.0939831 8.35817 0.145925 8.39374 0.204942 8.4105L1.74197 8.85008C2.43413 9.04538 3.06464 9.41517 3.57315 9.92409C4.08166 10.433 4.45117 11.064 4.64631 11.7567L5.08554 13.295C5.10229 13.354 5.13783 13.406 5.18678 13.443C5.23572 13.48 5.2954 13.5001 5.35675 13.5001C5.41809 13.5001 5.47777 13.48 5.52671 13.443C5.57566 13.406 5.6112 13.354 5.62795 13.295L6.06718 11.7567C6.26232 11.064 6.63183 10.433 7.14034 9.92409C7.64886 9.41517 8.27936 9.04538 8.97152 8.85008L10.5086 8.4105C10.5676 8.39374 10.6195 8.35817 10.6565 8.30918C10.6935 8.2602 10.7135 8.20048 10.7135 8.13908C10.7135 8.07769 10.6935 8.01797 10.6565 7.96898C10.6195 7.92 10.5676 7.88443 10.5086 7.86766Z"
      fill="#003FFF"
    />
    <path
      d="M12.9087 2.75173L12.2256 2.55636C11.918 2.46956 11.6378 2.30521 11.4118 2.07903C11.1858 1.85284 11.0215 1.5724 10.9348 1.26452L10.7396 0.58086C10.7298 0.556941 10.7131 0.536483 10.6916 0.522085C10.6702 0.507687 10.6449 0.5 10.6191 0.5C10.5932 0.5 10.568 0.507687 10.5465 0.522085C10.525 0.536483 10.5083 0.556941 10.4985 0.58086L10.3033 1.26452C10.2166 1.5724 10.0524 1.85284 9.82635 2.07903C9.60034 2.30521 9.32012 2.46956 9.01249 2.55636L8.32937 2.75173C8.30314 2.75918 8.28005 2.77499 8.26361 2.79676C8.24718 2.81853 8.23828 2.84507 8.23828 2.87236C8.23828 2.89965 8.24718 2.92619 8.26361 2.94796C8.28005 2.96973 8.30314 2.98554 8.32937 2.99299L9.01249 3.18836C9.32012 3.27516 9.60034 3.43951 9.82635 3.6657C10.0524 3.89188 10.2166 4.17233 10.3033 4.4802L10.4985 5.16386C10.506 5.19011 10.5218 5.21322 10.5435 5.22967C10.5653 5.24612 10.5918 5.25502 10.6191 5.25502C10.6463 5.25502 10.6728 5.24612 10.6946 5.22967C10.7164 5.21322 10.7321 5.19011 10.7396 5.16386L10.9348 4.4802C11.0215 4.17233 11.1858 3.89188 11.4118 3.6657C11.6378 3.43951 11.918 3.27516 12.2256 3.18836L12.9087 2.99299C12.935 2.98554 12.9581 2.96973 12.9745 2.94796C12.9909 2.92619 12.9998 2.89965 12.9998 2.87236C12.9998 2.84507 12.9909 2.81853 12.9745 2.79676C12.9581 2.77499 12.935 2.75918 12.9087 2.75173Z"
      fill="#003FFF"
    />
  </svg>
);

const truncateAtWord = (text, maxLength) => {
  if (!text || text.length <= maxLength) {
    return text;
  }

  const slice = text.slice(0, maxLength);
  const lastSpace = slice.lastIndexOf(" ");

  if (lastSpace > maxLength * 0.6) {
    return `${slice.slice(0, lastSpace).trimEnd()}…`;
  }

  return `${slice.trimEnd()}…`;
};

const SeeMoreButton = ({ onClick }) => (
  <button
    type="button"
    className="inline bg-transparent border-0 p-0 text-blue-600 cursor-pointer hover:underline"
    onClick={onClick}
  >
    See More
  </button>
);

const ExpandableDescription = ({ text, isPlaceholder = false }) => {
  const [expanded, setExpanded] = useState(false);
  const isTruncatable =
    text && text.length > OPTIMIZATION_IDEA_PREVIEW_MAX_LENGTH;
  const displayText =
    expanded || !isTruncatable
      ? text
      : truncateAtWord(text, OPTIMIZATION_IDEA_PREVIEW_MAX_LENGTH);

  return (
    <p className="text-xl mb-0 leading-relaxed light">
      {displayText}
      {isTruncatable && !expanded && (
        <>
          {" "}
          <SeeMoreButton onClick={() => setExpanded(true)} />
        </>
      )}
    </p>
  );
};

const MetricCard = ({
  label,
  value,
  change,
  comparisonAvailable,
  columnClass = "col-md-4 col-sm-4 col-xs-4",
}) => (
  <div
    className={`${columnClass} no-padding-sides`}
    style={{ paddingLeft: "4px", paddingRight: "4px" }}
  >
    <div className="border-solid border-[1px] border-slate-200 rounded-xl p-4 bg-white h-full">
      <div className="row hard-center">
        <span className="field-title">{label}</span>
      </div>
      <h4 className="report-count bold" style={{ margin: "5px 0px 0px" }}>
        {value}
      </h4>
      {comparisonAvailable && changeBadge(change)}
    </div>
  </div>
);

const OptimizationIdeaCard = ({ idea, index, align, slide }) => {
  const applyExperimentUrl = buildApplyExperimentUrl(slide, idea);
  const isPlaceholder = Boolean(
    idea?.placeholder || slide?.optimization_ideas_are_placeholder
  );

  return (
    <div
      className={`col-md-6 col-sm-12 ${
        align === "left" ? "no-padding-left" : "no-padding-right"
      }`}
      style={{ marginBottom: "12px" }}
    >
      <div
        className="rounded-xl overflow-hidden h-full bg-white"
        style={REMIX_GRADIENT_BORDER_STYLE}
      >
        <div className="flex items-center gap-2 px-5 py-4 border-b border-slate-100 min-w-0">
          <RemixSparkleIcon />
          <span
            className="text-[14px] font-medium text-[#003FFF] ellipsis min-w-0 flex-1"
            title={idea.title}
          >
            {idea.title}
          </span>
        </div>
        <div className="px-5 py-5 flex flex-col items-start">
          <ExpandableDescription
            text={idea.description}
            isPlaceholder={isPlaceholder}
          />
          {applyExperimentUrl && (
            <a
              href={applyExperimentUrl}
              className="inline-flex items-center mt-5 px-5 py-2.5 rounded-full text-xl font-semibold !text-white hover:!text-white focus:!text-white visited:!text-white border-none no-underline transition-opacity hover:opacity-90"
              style={{
                background: "linear-gradient(135deg, #4f6fff 0%, #003FFF 100%)",
                color: "#fff",
              }}
            >
              Create Split-Test →
            </a>
          )}
        </div>
      </div>
    </div>
  );
};

const badgeIcon = (badge) => {
  if (badge === "Top Revenue" || badge === "Top AOV") {
    return "dollar-sign";
  }

  return "link";
};

const WebsiteTopCampaignInsightSlide = (props) => {
  const { slide, currency, analysisPeriod, embedded = false, website } = props;

  if (!slide) {
    return null;
  }

  const goals = reportingGoals(website);
  const metrics = slide.metrics || {};
  const comparisonAvailable = slide.comparison_available !== false;
  const periodLabel = formatAnalysisPeriod(analysisPeriod);
  const horizontalPadding = embedded ? "0" : HORIZONTAL_PADDING;
  const goalMetrics = [];
  if (goals.checkouts) {
    goalMetrics.push(
      {
        label: "Orders",
        value: formatNumber(metrics.orders),
        change: metrics.orders_change,
      },
      {
        label: "Revenue",
        value: formatCurrency(metrics.revenue, currency),
        change: metrics.revenue_change,
      },
      {
        label: "AOV",
        value: formatCurrency(metrics.aov, currency),
        change: metrics.aov_change,
      }
    );
  }
  if (goals.bookings) {
    goalMetrics.push({
      label: "Bookings",
      value: formatNumber(metrics.bookings),
      change: metrics.bookings_change,
    });
  }
  if (goals.custom) {
    goalMetrics.push({
      label: goals.customGoalLabel,
      value: formatNumber(metrics.custom_goals),
      change: metrics.custom_goals_change,
    });
  }
  const metricRows = [
    [
      {
        label: "Viewers",
        value: formatNumber(metrics.views),
        change: metrics.views_change,
      },
      {
        label: "Conversions",
        value: formatNumber(metrics.conversions),
        change: metrics.conversions_change,
      },
      {
        label: "Conversion Rate",
        value: formatPercent(metrics.conversion_rate),
        change: metrics.conversion_rate_change,
      },
    ],
    goalMetrics,
  ];

  return (
    <div
      className="WebsiteTopCampaignInsightSlide"
      style={
        embedded
          ? {
              paddingTop: CAMPAIGN_INSIGHT_SECTION_PADDING,
              paddingBottom: CAMPAIGN_INSIGHT_SECTION_PADDING,
            }
          : undefined
      }
    >
      <div
        className="row hard-center"
        style={{
          marginLeft: 0,
          marginRight: 0,
          padding: embedded
            ? `0 ${horizontalPadding} 8px`
            : `13px ${horizontalPadding} 8px`,
          borderBottom: embedded ? "none" : "1px solid rgb(233, 235, 241)",
        }}
      >
        <div className="col-xs-10 no-padding-sides">
          {slide.badge && (
            <span className="inline-flex items-center px-2 py-1 mb-2 rounded text-base font-medium bg-blue-100 text-blue-700 uppercase">
              <FontAwesomeIcon icon={badgeIcon(slide.badge)} className="mr-2" />
              {slide.badge}
            </span>
          )}
          <h4 className="bold mb-0">
            <a
              href={slide.campaign_url}
              className="text-black hover:text-blue-600"
              target="_blank"
              rel="noopener noreferrer"
            >
              {slide.cta_type_label ? `${slide.cta_type_label} - ` : ""}
              {slide.name}
            </a>
          </h4>
          {periodLabel && (
            <p className="insight-description light text-xl mb-0 mt-1">
              Performance for {periodLabel}
            </p>
          )}
        </div>
        <div className="col-xs-2 no-padding-sides text-right">
          <a
            href={slide.campaign_url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block bg-white border-solid border-[1px] py-2 px-4 rounded-md border-slate-200 text-black text-xl hover:bg-blue-100 hover:border-blue-100 hover:text-blue-600"
          >
            Campaign →
          </a>
        </div>
      </div>

      <div style={{ padding: `12px ${horizontalPadding} 16px` }}>
        <div
          className="row hard-center"
          style={{ marginLeft: 0, marginRight: 0 }}
        >
          <div
            className="col-md-6 col-sm-12 no-padding-sides"
            style={{ paddingRight: COLUMN_GUTTER }}
          >
            <a
              href={slide.campaign_url}
              target="_blank"
              rel="noopener noreferrer"
            >
              <div
                className="border-solid border-slate-200 border-[1px] rounded-xl"
                style={{
                  backgroundImage: slide.screenshot_url
                    ? `url(${slide.screenshot_url})`
                    : "none",
                  backgroundColor: slide.screenshot_url ? undefined : "#f8fafc",
                  height: "240px",
                  backgroundSize: "cover",
                  backgroundPosition: "top center",
                  backgroundRepeat: "no-repeat",
                }}
              >
                {!slide.screenshot_url && (
                  <div className="flex items-center justify-center h-full light text-xl px-4 text-center">
                    Campaign preview unavailable
                  </div>
                )}
              </div>
            </a>
          </div>

          <div
            className="col-md-6 col-sm-12 no-padding-sides"
            style={{ paddingLeft: COLUMN_GUTTER }}
          >
            {metricRows.map((row, rowIndex) => (
              <div
                key={`metric-row-${rowIndex}`}
                className="row hard-center"
                style={{
                  marginLeft: 0,
                  marginRight: 0,
                  marginBottom: rowIndex === metricRows.length - 1 ? 0 : "12px",
                }}
              >
                {row.map((metric) => (
                  <MetricCard
                    key={metric.label}
                    label={metric.label}
                    value={metric.value}
                    change={metric.change}
                    comparisonAvailable={comparisonAvailable}
                    columnClass={
                      row.length === 4
                        ? "col-md-3 col-sm-3 col-xs-3"
                        : "col-md-4 col-sm-4 col-xs-4"
                    }
                  />
                ))}
              </div>
            ))}
          </div>
        </div>

        <div className="vertical-space" style={{ marginTop: "20px" }}>
          <h4 className="bold mb-3">Overview</h4>
          <div
            className={`rounded-xl px-5 py-5 border-solid border-[1px] ${
              slide.callout_is_placeholder
                ? "border-dashed border-slate-300 bg-slate-50"
                : "border-slate-200 bg-white"
            }`}
          >
            <p className="text-xl mb-0" style={OVERVIEW_TEXT_STYLE}>
              {slide.callout}
            </p>
          </div>
        </div>

        <div className="vertical-space" style={{ marginTop: "20px" }}>
          <h4 className="bold mb-3">Ideas To Test</h4>
          <div
            className="row hard-center"
            style={{ marginLeft: 0, marginRight: 0 }}
          >
            {(slide.optimization_ideas || []).slice(0, 2).map((idea, index) => (
              <OptimizationIdeaCard
                key={`${slide.id}-idea-${index}`}
                idea={idea}
                index={index}
                align={index % 2 === 0 ? "left" : "right"}
                slide={slide}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default WebsiteTopCampaignInsightSlide;
